import * as $ from 'jquery';
import { IDisposable, compositeDisposable, emptyDisposable, IEventAggregator, disposableSubscription } from 'common/barrels/core';
import { BehaviorSubject, Observable } from 'common/barrels/rx';
import { emptyGuid } from 'common/functions/guid';
import { camel } from 'common/functions/string';
import { IResourceProvider } from 'common/resourceprovider';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { IMethodRouter } from 'runtime/methodrouter';
import { IValueStore } from 'runtime/valuestore';
import { ControlArgs } from 'runtime/controls/controlargs';
import { CallQueue } from 'runtime/callqueue';
import { Subscription as ControlSubscription } from 'runtime/metadata/subscription';
import { BindingInfo } from 'runtime/metadata/bindinginfo';
import { and } from 'common/functions/observable';
import { ActivityExecutionRequested } from 'runtime/events/activityexecutionrequested';
import { ValidateRequested } from 'runtime/events/validaterequested';
import { applyBindings } from 'runtime/bindingengine';
import { Serializer } from 'common/serializer';

var marker = 'instance';

export class Control {
    protected $element: JQuery;
    protected eventAggregator: IEventAggregator;
    protected methods: IMethodRouter;
    protected subscriptions = emptyDisposable;
    protected values: IValueStore;
    protected config: any;
    protected fieldContext: string;
    protected bound: boolean;
    protected initialBindingValueReceived = false;
    protected writable = false;
    protected parentEnabled: boolean;
    protected enabled;
    protected visible;
    private isElementEnabledEverSet = false;
    private isElementVisibleEverSet = false;

    protected $tooltip: JQuery;
    protected $tabTarget: JQuery;

    protected resources: IResourceProvider;

    protected disposed = false;

    protected currentTooltip: string;

    protected container: ContainerControl;

    protected visibilitySubject = new BehaviorSubject<boolean>(false);
    visibilityStream: Observable<boolean>;

    protected serializer: Serializer;

    protected styles = new BehaviorSubject<string>(null);
    protected baseSubscriptions: IDisposable;


    constructor(args: ControlArgs) {
        this.$element = $(args.element);

        this.config = args.config;

        this.serializer = args.serializer;

        this.applyScrollVetoBehavior();

        if (!!this.config.zIndex)
            this.$element.css('z-index', this.config.zIndex);

        if (this.config.customStyles)
            this.$element.addClass(this.config.customStyles);

        this.methods = args.methods;
        this.eventAggregator = args.eventAggregator;
        this.values = args.values;

        this.fieldContext = args.fieldContext;
        this.resources = args.resources;
        this.container = args.container;

        this.visibilityStream = this.container ? and(this.container.visibilityStream, this.visibilitySubject) : this.visibilitySubject;

        this.placeElement(args.config);
        this.processSubscriptions(args.config);

        this.$element.data(marker, this);
        this.$element.on('remove', () => this.dispose());

        this.baseSubscriptions = disposableSubscription(
            this.styles.pairwise().subscribe(([o, n]) => {
                if (o !== null)
                    this.$element.removeClass(o);

                this.$element.addClass(n);
            })
        );

        if (this.config.customStyles)
            this.customStyles(this.config.customStyles);
    }

    protected initialize() {
        if (!this.isElementVisibleEverSet)
            this.isElementVisible(this.config.isElementVisible);

        if (!this.isElementEnabledEverSet)
            this.isElementEnabled(this.config.isElementEnabled);

        this.applyTabIndex();
    }

    static instance(element: HTMLElement): Control {
        return $(element).data(marker);
    }

    dispose() {
        this.baseSubscriptions.dispose();
        this.subscriptions.dispose();
        this.disposed = true;
    }

    private placeElement(config) {
        if (!config.margin)
            return;

        this.$element.css({
            left: (config.elementAlignment.horizontal === 'left' || config.elementAlignment.horizontal === 'stretch') ? config.margin.left : undefined,
            right: (config.elementAlignment.horizontal === 'right' || config.elementAlignment.horizontal === 'stretch') ? config.margin.right : undefined,
            top: (config.elementAlignment.vertical === 'top' || config.elementAlignment.vertical === 'stretch') ? config.margin.top : undefined,
            bottom: (config.elementAlignment.vertical === 'bottom' || config.elementAlignment.vertical === 'stretch') ? config.margin.bottom : undefined,
            width: config.elementAlignment.horizontal === 'stretch' ? undefined : config.elementWidth,
            height: config.elementAlignment.vertical === 'stretch' ? undefined : config.elementHeight
        });
    }

    public rightEdge() {
        if (!this.config.margin)
            return 0;

        // if docked left, the control requires left + width to be completely visible
        // if docked right, the control requires right + width to be completely visible
        // if stretched, the control is willing to yield ALL its space

        switch(this.config.elementAlignment.horizontal) {
            case 'left':
                return this.config.margin.left + this.config.elementWidth;
            case 'right':
                return this.config.margin.right + this.config.elementWidth;
            case 'stretch':
                return 0;
            default:
                throw new Error('unknown element alignment!');
        }
    }

    public bottomEdge() {
        if (!this.config.margin)
            return 0;

        switch(this.config.elementAlignment.vertical) {
            case 'top':
                return this.config.margin.top + this.config.elementHeight;
            case 'bottom':
                return this.config.margin.bottom + this.config.elementHeight;
            case 'stretch':
                return 0;
            default:
                throw new Error('unknown element alignment!');
        }
    }

    public stretchesHorizontally() {
        return this.config.elementAlignment.horizontal === 'stretch';
    }

    public stretchesVertically() {
        return this.config.elementAlignment.vertical === 'stretch';
    }

    public stretches() {
        return this.stretchesHorizontally() || this.stretchesVertically();
    }

    protected applyScrollVetoBehavior() {
        this.installScrollVeto(this.$element);
    }

    protected installScrollVeto($element: JQuery) {
        $element.on('scroll',() => {
            if ($element.scrollLeft() !== 0)
                $element.scrollLeft(0);

            if ($element.scrollTop() !== 0)
                $element.scrollTop(0);
        });
    }

    private processSubscriptions(config) {
        const subs = <ControlSubscription[]>config['subscriptions'];

        if (!subs || !Array.isArray(subs))
            return;

        subs.forEach(x => {
            var handler = () => {
                var calls = new CallQueue();

                const conditionMet = (x, invert: boolean) => (typeof x !== 'boolean') ? false
                        : (invert === true) ? (x === false)
                        : (x === true);

                x.actions.forEach(y => {
                    var prerequisiteWrapper = (y.executionPrerequisite === undefined)
                        ? z => z
                        : z => () => this.values.withValue(this.prerequisiteTypeToBindingType(y.executionPrerequisite.type), this.fieldContext, y.executionPrerequisite.name, condition => conditionMet(condition, y.executionPrerequisite.invert) ? z() : calls.proceed());

                    calls.enqueue(prerequisiteWrapper(y.isAction
                        ? () => this.methods.invoke(y.ownerName, camel(y.action), y.parameterValue, () => calls.proceed())
                        : () => this.eventAggregator.publish(new ActivityExecutionRequested(y.activity, this.fieldContext, null, () => calls.proceed()))
                    ));
                });
            };

            this.$element.bind(Control.makeFullEventName(x.event), handler);
        });
    }

    private prerequisiteTypeToBindingType(type: string): string {
        switch (type) {
            case 'Fields':
                return 'Field';
            case 'Rules':
                return 'Rule';
            case 'Variables':
                return 'Variable';
            default:
                throw new Error('Unknown ExecutionPrerequisite type! ' + '(' + type + ')');
        }
    }

    static makeFullEventName(eventName: string) {
        return 'events.' + eventName.replace(/ /g, '');
    }

    protected triggerEvent(eventName: string) {
        this.$element.triggerHandler(Control.makeFullEventName(eventName));
    }

    // visible / enabled methods and properties
    hide() { this.isElementVisible(false); }

    show() { this.isElementVisible(true); }

    enable() { this.isElementEnabled(true); }

    disable() { this.isElementEnabled(false); }

    isElementEnabled(value: boolean) {
        this.isElementEnabledEverSet = true;
        if (this.enabled !== value) {
            this.enabled = value;
            this.applyEnabledState(this.derivedEnabledState());
        }
    }

    isParentEnabled(value: boolean) {
        if (this.parentEnabled !== value) {
            this.parentEnabled = value;
            this.applyEnabledState(this.derivedEnabledState());
        }
    }

    isElementVisible(value: boolean) {
        this.isElementVisibleEverSet = true;
        this.visible = value;

        var derivedValue = this.derivedVisibilityState();
        this.applyVisibleState(derivedValue);
        this.visibilitySubject.next(derivedValue);
    }

    protected derivedVisibilityState() {
        return this.visible === true ? true
                : this.visible === false ? false
                : this.config.isElementVisible;
    }

    protected applyEnabledState(value: boolean) { }

    protected reapplyEnabledState() {
        this.applyEnabledState(this.derivedEnabledState());
    }

    protected applyVisibleState(value: boolean) {
        if (value === true)
            this.$element.show();

        if (value === false)
            this.$element.hide();
    }

    protected derivedEnabledState() {
        var enabledOrPrebound = this.enabled !== null ? this.enabled : this.config.isElementEnabled;

        return this.bound
            ? this.writable && this.initialBindingValueReceived && (this.parentEnabled || false) && enabledOrPrebound
            : (this.parentEnabled || false) && enabledOrPrebound;
    }

    protected writableAgnosticDerivedEnabledState() {
        var enabledOrPrebound = this.enabled !== null ? this.enabled : this.config.isElementEnabled;
        return (this.parentEnabled || false) && enabledOrPrebound;
    }

    protected isReady() {
        return this.derivedEnabledState() !== false && this.visible !== false;
    }

    protected checkInitialValueReceived() {
        if (!this.initialBindingValueReceived) {
            this.initialBindingValueReceived = true;
            this.applyEnabledState(this.derivedEnabledState());
        }
    }

    applyTooltip(target?: JQuery) {
        if (target)
            this.$tooltip = target;

        this.toolTip(this.config.toolTip);
    }

    updateTooltipTarget(target?: JQuery) {
        var existing = this.currentTooltip;
        this.toolTip(null);
        this.$tooltip = target;
        this.toolTip(existing);
    }

    toolTip(value: string) {
        this.currentTooltip = value;
        var $e = (this.$tooltip || this.$element);

        if (value === null || value === undefined || value === '')
            $e.removeAttr('title');
        else
            $e.attr('title', value);
    }

    customStyles(value: string) {
        this.styles.next(value);
    }

    protected tabIndexElements() {
        return $();
    }

    protected applyTabIndex() {
        // the tabindex mechanism has been reworked such that all elements are ordered according to their taborder properties, allowing delegation to the browsers default behaviour
        // because some elements (such as divs) require tabindex to be set in order to receive focus, all elements are set to tabindex 1
        this.tabIndexElements().prop('tabindex', 0);
    }

    protected setFocusToFirstFocusableSubElement($container: JQuery) {
        const controls = $container.find('.control-container').toArray().map(x => $(x).data('control')).filter(x => !!x);
        const focusable = controls.map(x => x.tabIndexElements()).filter(x => x.length > 0);
        if (focusable.length > 0 && focusable[0])
            focusable[0].focus();
    }

    'show Popup'(viewName: string) {
        this.methods.invoke('Document', 'show Popup', { viewName: viewName, fieldContext: this.fieldContext }, () => { });
    }

    validate(ruleName: string) {
        this.eventAggregator.publish(new ValidateRequested(this, ruleName));
    }

    validated() {
        this.$element.triggerHandler(Control.makeFullEventName('Validated'));
    }

    validationFailed() {
        this.$element.triggerHandler(Control.makeFullEventName('Validation Failed'));
    }

    validationSucceeded() {
        this.$element.triggerHandler(Control.makeFullEventName('Validation Succeeded'));
    }

    validatedWithWarnings() {
        this.$element.triggerHandler(Control.makeFullEventName('Validated with Warnings'));
    }

    protected processBindings(config): IDisposable {
        return compositeDisposable((<BindingInfo[]>(config.bindings || [])).map(binding => this.values.createBinding(binding, this, this.$element, this.fieldContext)));
    }

    protected fieldBindingSource(target: string) {
        const camelTarget = camel(target);
        const matches = (<BindingInfo[]>this.config.bindings || []).filter(x => x.type === 'Field' && camel(x.target) === camelTarget).map(x => x.source);

        if (matches.length > 1)
            throw new Error('did not expect multiple matching bindings!');

        return matches.length === 1 ? matches[0] : null;
    }

    protected bindElements($elements, container: ContainerControl, fieldContext?: string, methods?: IMethodRouter) {
        applyBindings($elements, fieldContext || this.fieldContext, this.eventAggregator, methods || this.methods, this.values, this.resources, container, this.serializer);
    }

    protected hasBinding(target: string) {
        return (<BindingInfo[]>this.config.bindings || []).some(x => camel(x.target) === target);
    }

    protected hasFieldBinding(target: string) {
        return (<BindingInfo[]>this.config.bindings || []).some(x => camel(x.target) === target && x.type === 'Field');
    }

    protected getFieldBinding(target: string) {
        const camelTarget = camel(target);
        const matches = (<BindingInfo[]>this.config.bindings || []).filter(x => x.type === 'Field' && camel(x.target) === camelTarget);

        if (matches.length > 1)
            throw new Error('did not expect multiple matching bindings!');

        return matches.length === 1 ? matches[0] : null;
    }

    protected hasSubscriptions(event: string) {
        return (<ControlSubscription[]>this.config.subscriptions || []).some(x => camel(x.event) === camel(event));
    }

    protected managePrimaryBinding(propertyName: string): IDisposable {
        if (this.hasBinding(propertyName)) {
            this.bound = true;

            const fieldBinding = this.getFieldBinding(propertyName);

            if (fieldBinding !== null) {                
                return this.values.bindSecurity(fieldBinding.ignoreFieldContext === 'true' ? emptyGuid : this.fieldContext, this.fieldBindingSource(propertyName), x => {
                    this.writable = x;
                    this.applyEnabledState(this.derivedEnabledState());
                });
            } else {
                this.writable = true;
                return emptyDisposable;
            }
        } else { 
            // no binding, so we are writable by default
            this.writable = true;
            return emptyDisposable;
        }
    }

    protected applyConfigNamesake(...args) {
        args.forEach(x => this[x](this.config[x]));
    }

    protected boolOrDefault(value: boolean, defaultValue: boolean) {
        return value === true ? true
                : value === false ? false
                : defaultValue;
    }
}

// not sure where / when this is used, but it looks like it might be important
export interface IViewModelRoot {
    eventAggregator: IEventAggregator;
    resources: IResourceProvider;
}

// this is disgusting... it should be booted to some other location
//export interface IKnockoutBindingContext extends KnockoutBindingContext {
//    $root: IViewModelRoot;
//}